import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Tag, Typography } from 'antd';
import { ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import { priorityApi } from '../services/api';

const { Text } = Typography;

// 三个勾选行（对应表格的行；「普通」为原「常规」）
const ROWS = [
  { key: 'urgent', label: '紧急重要!', color: '#ff4d4f', bg: '#fff1f0' },
  { key: 'high', label: '一般重要', color: '#fadb14', bg: '#fffbe6' },
  { key: 'normal', label: '普通', color: '#52c41a', bg: '#f6ffed' },
];
const POLICY_LABEL = { urgent: '紧急重要', high: '一般重要', normal: '普通' };

/**
 * 优先级规则（前端只读展示页）
 * - 维度判定矩阵与后端管理台同步显示【全部维度】（避免调度人员误以为只有客户维度），
 *   但表格整体只读，前端不可修改矩阵任何内容（无任何点击/编辑入口）。
 * - 客户类型维度（code=customer）的三级分类与勾选行，仅能通过「客户管理」的
 *   客户添加/修改等级表单更新，由后端自动同步到矩阵。
 * - 其他维度（异常类型等）由管理端在后台维护，前端只读展示。
 * - 优先级确定后随之确定跟进频次、弹窗、排序（策略表只读展示）。
 */
export default function PriorityRules() {
  const [dimensions, setDimensions] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const data = await priorityApi.getDimensions();
      setDimensions(data.dimensions || []);
      setPolicies(data.policies || []);
    } catch (e) {} finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const policyOf = (rowKey) => policies.find((p) => p.priority === rowKey);

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2>优先级规则</h2>
        </div>
        <Button icon={<ReloadOutlined />} onClick={fetchAll}>刷新</Button>
      </div>

      {/* ===== 维度判定矩阵（全量维度，与管理台同步显示；整体只读） ===== */}
      <Card
        className="card-shadow"
        style={{ marginBottom: 16, overflowX: 'auto' }}
        title={(
          <span>
            维度判定矩阵
            <Tag color="default" style={{ marginLeft: 8 }}>只读展示</Tag>
            {loading && <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>同步中...</Text>}
          </span>
        )}
      >
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 760 }}>
          <thead>
            {/* 第一行：维度组（客户类型标识同步来源） */}
            <tr>
              <th rowSpan={2} style={thStyle}>调度订单优先级</th>
              {dimensions.map((d) => (
                <th key={`g-${d.id}`} colSpan={Math.max((d.options || []).length, 1)} style={thStyle}>
                  {d.name}
                  {d.code === 'customer' ? (
                    <Tag color="blue" style={{ marginLeft: 6 }} icon={<SyncOutlined />}>客户管理同步</Tag>
                  ) : (
                    <Tag style={{ marginLeft: 6 }}>管理端维护</Tag>
                  )}
                </th>
              ))}
              <th rowSpan={2} style={thStyle}>判断</th>
              <th rowSpan={2} style={thStyle}>跟进频次</th>
            </tr>
            {/* 第二行：选项子列 */}
            <tr>
              {dimensions.map((d) => (
                (d.options || []).length
                  ? d.options.map((opt) => (
                    <th key={`o-${opt.id}`} style={{ ...thStyle, fontWeight: 400, background: '#fff' }}>
                      {opt.label}
                    </th>
                  ))
                  : <th key={`e-${d.id}`} style={{ ...thStyle, fontWeight: 400, background: '#fff' }}>待添加</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map((row) => {
              const pol = policyOf(row.key);
              return (
                <tr key={row.key}>
                  <td style={{ ...tdStyle, background: row.bg }}>
                    <Text strong style={{ color: row.key === 'urgent' ? '#ff4d4f' : '#333' }}>{row.label}</Text>
                  </td>
                  {dimensions.map((d) => (
                    (d.options || []).length
                      ? d.options.map((opt) => {
                        const checked = opt.trigger_level === row.key;
                        return (
                          <td
                            key={`c-${row.key}-${opt.id}`}
                            style={{ ...tdStyle, background: checked ? row.bg : '#fff' }}
                          >
                            {/* 勾选的显示勾选，未勾选的不显示；整体只读，无点击入口 */}
                            {checked && (
                              <span style={{ color: row.key === 'high' ? '#d48806' : row.color, fontSize: 18 }}>✔</span>
                            )}
                          </td>
                        );
                      })
                      : <td key={`c-${row.key}-e-${d.id}`} style={tdStyle} />
                  ))}
                  <td style={tdStyle}>{pol?.judge_text || '-'}</td>
                  <td style={tdStyle}>{pol?.follow_up_text || '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          客户等级映射：重要客户 → 紧急重要；中等客户 → 一般重要；一般普通客户 → 普通。
          在「客户管理」录入客户或修改客户等级后，矩阵中客户类型维度自动同步。
        </Text>
      </Card>

      {/* ===== 提醒策略表（只读） ===== */}
      <Card className="card-shadow" title="优先级策略（跟进频次 / 弹窗提醒间隔）">
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={thStyle}>优先级</th>
              <th style={thStyle}>判断</th>
              <th style={thStyle}>跟进频次</th>
              <th style={thStyle}>弹窗提醒间隔</th>
            </tr>
          </thead>
          <tbody>
            {policies.map((p) => {
              const row = ROWS.find((r) => r.key === p.priority) || {};
              return (
                <tr key={p.id}>
                  <td style={{ ...tdStyle, background: row.bg }}><Text strong>{p.label || POLICY_LABEL[p.priority]}</Text></td>
                  <td style={tdStyle}>{p.judge_text || '-'}</td>
                  <td style={tdStyle}>{p.follow_up_text || '-'}</td>
                  <td style={tdStyle}>
                    每 {p.reminder_hours} 小时（{p.reminder_hours >= 24 ? `${Math.round(p.reminder_hours / 24)} 天` : ''}）
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          策略为系统默认，前端不可更改；如需调整请由管理端在后台维护。
        </Text>
      </Card>
    </div>
  );
}

const thStyle = {
  border: '1px solid #d9d9d9',
  padding: '8px 10px',
  fontSize: 13,
  textAlign: 'center',
  background: '#fafafa',
  minWidth: 110,
};

const tdStyle = {
  border: '1px solid #d9d9d9',
  padding: '8px 10px',
  fontSize: 13,
  textAlign: 'center',
};
